import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import api from "@/lib/api";
import Layout from "@/components/Layout";
import LoginPage from "@/pages/LoginPage";
import LandingPage from "@/pages/LandingPage";
import DashboardPage from "@/pages/DashboardPage";
import ResidentsPage from "@/pages/ResidentsPage";
import ResidentDetailPage from "@/pages/ResidentDetailPage";
import MedicationRoundPage from "@/pages/MedicationRoundPage";
import StockPage from "@/pages/StockPage";
import StaffPage from "@/pages/StaffPage";
import FamilyPortalPage from "@/pages/FamilyPortalPage";
import SuperAdminPage from "@/pages/SuperAdminPage";
import SettingsPage from "@/pages/SettingsPage";
import UsersPage from "@/pages/UsersPage";
import DailyLogsPage from "@/pages/DailyLogsPage";
import IncidentsPage from "@/pages/IncidentsPage";
import MessagesPage from "@/pages/MessagesPage";
import FamilyDashboardPage from "@/pages/FamilyDashboardPage";
import DoctorsPage from "@/pages/DoctorsPage";
import PortalConfigPage from "@/pages/PortalConfigPage";
import { Toaster } from "@/components/ui/sonner";

const AuthContext = createContext(null);
const ThemeContext = createContext(null);

export const useAuth = () => useContext(AuthContext);
export const useTheme = () => useContext(ThemeContext);

function ThemeProvider({ children }) {
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem("gerion_dark") === "true");
  const [accentColor, setAccentColor] = useState(() => localStorage.getItem("gerion_accent") || "violet");

  useEffect(() => {
    const root = document.documentElement;
    if (darkMode) { root.classList.add("dark"); } else { root.classList.remove("dark"); }
    localStorage.setItem("gerion_dark", darkMode);
  }, [darkMode]);

  useEffect(() => {
    document.documentElement.setAttribute("data-accent", accentColor);
    localStorage.setItem("gerion_accent", accentColor);
  }, [accentColor]);

  const toggleDark = () => setDarkMode(prev => !prev);

  return (
    <ThemeContext.Provider value={{ darkMode, toggleDark, accentColor, setAccentColor }}>
      {children}
    </ThemeContext.Provider>
  );
}

function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [impersonatedTenant, setImpersonatedTenant] = useState(() => {
    const saved = localStorage.getItem("gerion_tenant_override");
    const savedName = localStorage.getItem("gerion_tenant_override_name");
    return saved ? { id: saved, name: savedName } : null;
  });

  const checkAuth = useCallback(async () => {
    const token = localStorage.getItem("gerion_token");
    if (!token) {
      setLoading(false);
      return;
    }
    try {
      const res = await api.get("/auth/me");
      setUser(res.data);
    } catch {
      localStorage.removeItem("gerion_token");
      localStorage.removeItem("gerion_user");
    }
    setLoading(false);
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  const login = async (email, password) => {
    const res = await api.post("/auth/login", { email, password });
    localStorage.setItem("gerion_token", res.data.token);
    localStorage.setItem("gerion_user", JSON.stringify(res.data.user));
    setUser(res.data.user);
    if (res.data.tenant_accent) {
      localStorage.setItem("gerion_accent", res.data.tenant_accent);
      document.documentElement.setAttribute("data-accent", res.data.tenant_accent);
    }
    if (res.data.tenant_features) {
      localStorage.setItem("gerion_features", JSON.stringify(res.data.tenant_features));
    }
    return res.data;
  };

  const logout = () => {
    localStorage.removeItem("gerion_token");
    localStorage.removeItem("gerion_user");
    localStorage.removeItem("gerion_tenant_override");
    localStorage.removeItem("gerion_tenant_override_name");
    setUser(null);
    setImpersonatedTenant(null);
  };

  const impersonate = (tenantId, tenantName) => {
    if (tenantId) {
      localStorage.setItem("gerion_tenant_override", tenantId);
      localStorage.setItem("gerion_tenant_override_name", tenantName);
      setImpersonatedTenant({ id: tenantId, name: tenantName });
    } else {
      localStorage.removeItem("gerion_tenant_override");
      localStorage.removeItem("gerion_tenant_override_name");
      setImpersonatedTenant(null);
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, impersonatedTenant, impersonate }}>
      {children}
    </AuthContext.Provider>
  );
}

function ProtectedRoute({ children, staffOnly }) {
  const { user, loading } = useAuth();
  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-[#F8F8F8]">
      <div className="text-[#757575] text-lg font-medium">Cargando...</div>
    </div>
  );
  if (!user) return <Navigate to="/login" replace />;
  if (staffOnly && user.role === "family_user") return <Navigate to="/family" replace />;
  return <Layout>{children}</Layout>;
}

function App() {
  return (
    <ThemeProvider>
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/landing" element={<LandingPage />} />
          <Route path="/family-portal/:accessCode" element={<FamilyPortalPage />} />
          <Route path="/family" element={<ProtectedRoute><FamilyDashboardPage /></ProtectedRoute>} />
          <Route path="/admin" element={<ProtectedRoute staffOnly><SuperAdminPage /></ProtectedRoute>} />
          <Route path="/dashboard" element={<ProtectedRoute staffOnly><DashboardPage /></ProtectedRoute>} />
          <Route path="/residents" element={<ProtectedRoute staffOnly><ResidentsPage /></ProtectedRoute>} />
          <Route path="/residents/:id" element={<ProtectedRoute staffOnly><ResidentDetailPage /></ProtectedRoute>} />
          <Route path="/medication-round" element={<ProtectedRoute staffOnly><MedicationRoundPage /></ProtectedRoute>} />
          <Route path="/stock" element={<ProtectedRoute staffOnly><StockPage /></ProtectedRoute>} />
          <Route path="/staff" element={<ProtectedRoute staffOnly><StaffPage /></ProtectedRoute>} />
          <Route path="/daily-logs" element={<ProtectedRoute staffOnly><DailyLogsPage /></ProtectedRoute>} />
          <Route path="/incidents" element={<ProtectedRoute staffOnly><IncidentsPage /></ProtectedRoute>} />
          <Route path="/messages" element={<ProtectedRoute staffOnly><MessagesPage /></ProtectedRoute>} />
          <Route path="/settings" element={<ProtectedRoute staffOnly><SettingsPage /></ProtectedRoute>} />
          <Route path="/users" element={<ProtectedRoute staffOnly><UsersPage /></ProtectedRoute>} />
          <Route path="/doctors" element={<ProtectedRoute staffOnly><DoctorsPage /></ProtectedRoute>} />
          <Route path="/portal-config" element={<ProtectedRoute staffOnly><PortalConfigPage /></ProtectedRoute>} />
          <Route path="/" element={<LandingPage />} />
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" />
    </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
